import { InfoOutlineIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Radio,
  RadioGroup,
  Tooltip,
  Button
} from '@chakra-ui/react';
import { TextInput, useBoolean, usePaginationSessionStorage, useTabStorage } from '@laxmimanogna/code-quick-components';
import React, { useContext, useState, useEffect, useMemo } from 'react';
import Select from 'react-select'
import AppColors from '../../constants/AppColors';
import { FONT_FAMILY } from '../../constants/Theme';
import { withMultiProviders } from '../../hoc/withProvider';
import useCQMediaQuery from '../../hooks/useCQMediaQuery';
import MyChartProvider, {
  MyChartContext,
} from '../../providers/MyChartProvider';
import AllCharts from './components/tables/AllCharts';
import ArchivedCharts from './components/tables/ArchivedCharts';
import RebuttedCharts from './components/tables/RebuttedCharts';
import AuditSheetProvider, { AuditSheetContext } from '../../providers/AuditSheetProvider';
import NewChartsTable from './components/tables/NewChartsTable';
import { CHART_FILTER_INFO, MY_CHARTS_FILTER_OPTIONS, MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP } from '../../constants/constants';
import { MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME } from './constants';
import CQContainer from '../../components/layout/CQContainer';


const DropdownComponent = ({
  options,
  setSelected,
  selected,
  placeholder = "Select Options",
  isDisabled=false,
  disabledText = "Dropdown disabled",
  isLoading=false,
  isClearable=true,
  isSearchable=true
}) => {
  const handleChange = (option) => {
    setSelected(option);
  }

  return (
    <div
    style={{
      flex:1,
    }}>
      <Select 
      options={options}
      onChange={handleChange}
      isClearable={isClearable}
      placeholder={placeholder}
      isDisabled={isDisabled}
      value={selected}
      isLoading={isLoading}
      isSearchable={isSearchable}
      />
    
    </div> 
  )
}

const MyChartsContainerV2 = props => {
  const myChartsTab = [
    {
      title: 'ALL',
      header: 'Audit Uploads',
      component: NewChartsTable,
    },
  ];
  const {
    getPaginationSession,
    setPaginationSession
  } = usePaginationSessionStorage();
  const DEFAULT_FILTER_NAME = useMemo(() => "All", []);
  const DEFAULT_FILTER_POS = useMemo(() => MY_CHARTS_FILTER_OPTIONS.indexOf(DEFAULT_FILTER_NAME), [DEFAULT_FILTER_NAME])
  const myChartContext = useContext(MyChartContext);
  const auditSheetContext = useContext(AuditSheetContext);
  const [resetFilterTrigger, rftState] = useBoolean(null);

  const { 
    handleApplyFilter, 
    filterParams, 
    getAllCharts, 
    getArchivedCharts,
    isAllChartLoading,
  } =
    myChartContext;

  const tabStorage = useTabStorage();
  const [selectedTab, setSelectedTab] = useState(tabStorage.getTab() || 0);

  useEffect(() => {tabStorage.setTab(selectedTab)}, [selectedTab]);

  const dropdownOptions = useMemo(
    () => MY_CHARTS_FILTER_OPTIONS.map(val => ({ value: val, label: val })),
    [MY_CHARTS_FILTER_OPTIONS]
  );

  const [selectedFilter, setSelectedFilter] = useState(() => ({
    value: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS],
    label: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS]
  }));

  useEffect(() => {
    if (!selectedFilter.value) {
      return;
    }
    tabStorage.setTab(MY_CHARTS_FILTER_OPTIONS.indexOf(selectedFilter.value))
  }, [selectedFilter]);

  const marginHorizontal = useCQMediaQuery({
    lg: '12%',
    md: '6%',
    sm: '2%',
  });

  const onSearchAction = () => {
    handleApplyFilter({
      ...filterParams,
      triggerSearch: !filterParams.triggerSearch,
    });
  };

  const onFilterChange = e => {
    handleApplyFilter({
      ...filterParams,
      searchText: e.target.value
    })
  };

  const onOpenNotes = (obj, batchId, tabType = undefined) => {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
          tabType: tabType
        })
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: "Sheet1",
          page: 1,
          tabType: tabType
        })
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        batchId,
        sheet_name: "Sheet1",
        reverse: true,
        page: 1,
        tabType: tabType
      })
    }

  }

  return (
      <Box>
        
        <CQContainer>
          <HStack spacing={2} >
          <Heading
            my={3}
            fontSize={'24px'}
            fontFamily={FONT_FAMILY.baiJamurjee}
            fontWeight="bold"
          >
            My Charts
          </Heading>
          <Tooltip p={3} bg="whiteAlpha.900"
            color="black"
            borderRadius="md"
            border="1px solid #43dde0" label={
              <Box>
                <>
                  <p>
                    <b>My Charts</b> tab displays only the charts that you have personally uploaded. This helps you manage and review your individual contributions separately.
                  </p>
                </>
              </Box>
            } placement="top-end">
            <Icon as={InfoOutlineIcon} w={4} h={4} cursor="pointer" _hover={{ color: AppColors.secondary, transition: 'color 0.3s ease-in-out' }} color={AppColors.primary} />
          </Tooltip></HStack>

        <Box
          shadow={'md'}
          bg="white"
          p={4}
          w="100%"
          style={{ borderRadius: '12px' }}
        >
          <HStack w={'100%'} justifyContent={'space-between'} pb={3} borderBottom="1px solid" borderColor="gray.100">
            <Box width={"35%"}>
              <HStack>
                <Tooltip
                  p={3}
                  bg="whiteAlpha.900"
                  color="black"
                  borderRadius="md"
                  border="1px solid #43dde0"
                  closeDelay={100}
                  label={
                    <Box>
                      {CHART_FILTER_INFO.map((item, index) => (
                        <Box key={item.name} mb={index !== CHART_FILTER_INFO.length - 1 ? 2 : 0}>
                          <Box fontWeight="600" fontSize="sm">
                            {item.name}
                          </Box>

                          <Box fontSize="s" color="gray.600">
                            {item.definition}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  }
                  placement="bottom-end"
                >
                  <Box>
                    <Icon
                      as={InfoOutlineIcon}
                      w={4}
                      h={4}
                      cursor="pointer"
                      color={AppColors.primary}
                      _hover={{
                        color: AppColors.secondary,
                        transition: "color 0.3s ease-in-out",
                      }}
                    />
                  </Box>
                </Tooltip>
                <Box width={"60%"}>
                  <DropdownComponent
                    options={dropdownOptions}
                    selected={selectedFilter}
                    setSelected={option => {
                      setSelectedFilter(option);
                      handleApplyFilter({
                        ...filterParams, 
                        triggerSearch: !filterParams.triggerSearch,
                        chartStatusType: option.value
                      });
                    }}
                    placeholder=''
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={isAllChartLoading}
                    isLoading={isAllChartLoading}
                  />
                </Box>
              </HStack>
            </Box>
            <Box>
              <HStack>
                <Button
                  mr={1}
                  mb={2}
                  isDisabled={isAllChartLoading}
                  cursor={isAllChartLoading ? "not-allowed" : "pointer"}
                  color={AppColors.secondary}
                  fontSize="15px"
                  display="flex"
                  alignItems="center"
                  _hover={
                    !isAllChartLoading
                      ? {
                          color: "red.500",
                          textDecoration: "underline",
                        }
                      : undefined
                  }
                  onClick={() => {
                    setSelectedFilter(dropdownOptions[DEFAULT_FILTER_POS]);
                    setPaginationSession(
                      MY_CHARTS_PAGE_NAME, 
                      MY_CHARTS_TABLE_NAME, 
                      {
                        ...getPaginationSession(MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME),
                        page: 1,
                        size: 10
                      }
                    )
                    handleApplyFilter({
                      searchText: "",
                      triggerSearch: !filterParams?.triggerSearch,
                      ordering: "",
                      chartStatusType: DEFAULT_FILTER_NAME,
                    });
                    rftState.toggle();
                  }}
                >
                  Clear Filters
                </Button>
                <TextInput
                  isDisabled={isAllChartLoading}
                  borderRadius={'md'}
                  value={filterParams?.searchText}
                  onChange={e => onFilterChange(e)}
                  onKeyDown={e => {
                    if (e.keyCode === 13) {
                      onSearchAction();
                    }
                  }}
                  mb="2"
                  rightIconProps={{ zIndex: 0 }}
                  placeholder="Search"
                  rightIcon={
                    <SearchIcon
                      onClick={onSearchAction}
                      color={AppColors.gray}
                    />
                  }
                />
              </HStack>
            </Box>
          </HStack>

          <Box pt={3}>
            {myChartsTab.map((aa, index) => (
               (
                <aa.component 
                key={`my-charts-panel-${index}`} 
                onOpenNotes={onOpenNotes}
                PAGE_NAME={MY_CHARTS_PAGE_NAME}
                TABLE_NAME={MY_CHARTS_TABLE_NAME}
                filterResetTrigger={resetFilterTrigger}
                />
              )
            ))}
          </Box>
        </Box>
        </CQContainer>
      </Box>
  );
};

export default withMultiProviders([MyChartProvider, AuditSheetProvider], MyChartsContainerV2);